import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Check, X, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";

interface EditableFieldProps {
    value: string | number | undefined | null;
    onSave: (newValue: string) => Promise<void> | void;
    label?: string;
    type?: "text" | "number" | "date" | "email";
    className?: string;
    placeholder?: string;
    formatValue?: (val: any) => string;
    disabled?: boolean;
}

export function EditableField({
    value,
    onSave,
    type = "text",
    className,
    placeholder = "Click to edit",
    formatValue,
    disabled = false,
}: EditableFieldProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value?.toString() || "");
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        // Sync local state when external value changes (only if not editing)
        if (!isEditing) {
            setTempValue(value?.toString() || "");
            setErrorMessage(null);
        }
    }, [value, isEditing]);


    const handleSave = async () => {
        if (tempValue === value?.toString()) {
            setIsEditing(false);
            setErrorMessage(null);
            return;
        }

        try {
            setIsLoading(true);
            setErrorMessage(null);
            await onSave(tempValue);
            setIsEditing(false);
        } catch (error: any) {
            console.error("Failed to save", error);
            setErrorMessage(error.message || "Failed to save");
            // Do NOT exit editing mode
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setTempValue(value?.toString() || "");
        setIsEditing(false);
        setErrorMessage(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSave();
        } else if (e.key === "Escape") {
            handleCancel();
        }
    };

    if (isEditing) {
        return (
            <div className={cn("flex flex-col w-full", className)}>
                <div className="flex items-center gap-1 w-full">
                    <Input
                        ref={inputRef}
                        type={type}
                        value={tempValue}
                        onChange={(e) => {
                            setTempValue(e.target.value);
                            if (errorMessage) setErrorMessage(null);
                        }}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading}
                        className={cn("h-8 text-sm", errorMessage && "border-red-500 focus-visible:ring-red-500")}
                        placeholder={placeholder}
                    />
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={handleSave}
                        disabled={isLoading}
                    >
                        <Check className="h-4 w-4" />
                    </Button>
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={handleCancel}
                        disabled={isLoading}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
                {errorMessage && (
                    <span className="text-xs text-red-500 mt-1 ml-1">{errorMessage}</span>
                )}
            </div>
        );
    }

    const displayValue = formatValue ? formatValue(value) : (value || <span className="text-muted-foreground italic text-xs">{placeholder}</span>);

    return (
        <div
            className={cn(
                "flex items-center justify-between min-h-[28px] min-w-0",
                isLoading && "opacity-50 cursor-wait",
                className
            )}
        >
            <span className="min-w-0 px-2 py-1 -mx-2" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {displayValue}
            </span>
            <button
                type="button"
                disabled={isLoading || disabled}
                onClick={() => setIsEditing(true)}
                className="shrink-0 p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none"
                title={disabled ? "Read only" : "Edit"}
                aria-label={disabled ? "Read only" : "Edit"}
            >
                <Pencil className="h-3 w-3" />
            </button>
        </div>
    );
}
